import { useEffect, useState } from "react";
import { Breadcrumb, Button, Descriptions, Image, Skeleton, Tag } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";
import classes from "./SingleEvent.module.scss";

import ClampText from "../../../generic components/TextTruncate/TextTruncate";
import { ArrowLeftOutlined } from "@ant-design/icons";

export default function SingleEvent() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await axios.get(routes.getSingleEvent(id));
      setData(data?.event || []);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch (e) {
      // silent
    } finally {
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  if (!data) return;

  return (
    <div className={classes.project}>
      <div>
        {!loading && data.bannerImage ? (
          <div
            style={{
              height: 400,
              background: `url(${data.bannerImage}) center/cover no-repeat`,
            }}
          />
        ) : (
          <Skeleton.Image
            active
            style={{
              width: "100vw",
              height: 400,
              zIndex: 100,
            }}
          />
        )}
        <div className={classes.project__head}>
          <div className={classes.project__head__bread_crumbs}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Back
            </Button>
            <Breadcrumb
              items={[
                {
                  title: (
                    <Link style={{ cursor: "pointer" }} to="/capacity-projects">
                      Projects
                    </Link>
                  ),
                },
                data
                  ? {
                      title: (
                        <Link style={{ cursor: "pointer" }} to={`/events`}>
                          {"Events"}
                        </Link>
                      ),
                    }
                  : { title: "Project" },
                { title: data?.title || "Update" },
              ]}
            />
          </div>
          <div className={classes.project__title}>
            <h2>{data.title}</h2>
            <div style={{ marginBottom: 8 }}>
              {data &&
                data.tags.length > 0 &&
                data.tags.map((tag) => <Tag color="blue">{tag}</Tag>)}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.project__container}>
        <div className={classes.project__body}>
          {loading ? (
            <Skeleton active />
          ) : (
            data.summary && (
              <div className={classes.project__body__box}>
                <h2 className={classes.project__body__title}>Summary</h2>
                <p
                  style={{ lineHeight: 1.7 }}
                  dangerouslySetInnerHTML={{ __html: data.summary }}
                />
              </div>
            )
          )}

          {!loading ? (
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <Image.PreviewGroup>
                {data.gallery &&
                  data.gallery.length > 0 &&
                  data.gallery.map((img, index) => (
                    <Image
                      key={index}
                      className={`${classes.mediaImg}`}
                      src={img}
                      alt={`${data.name} hero`}
                      loading="lazy"
                      itemProp="image"
                      width={150}
                      height={150}
                    />
                  ))}
              </Image.PreviewGroup>
            </div>
          ) : (
            <Skeleton.Image style={{ width: "100%", minHeight: 400 }} />
          )}
          {loading ? (
            <Skeleton active />
          ) : (
            data.contentHtml && (
              <div className={classes.project__body__box}>
                <h2 className={classes.project__body__title}>Overview</h2>
                <p
                  style={{ lineHeight: 1.7 }}
                  dangerouslySetInnerHTML={{ __html: data.contentHtml }}
                />
              </div>
            )
          )}

          {data && data.length > 0 && (
            <>
              <div className={classes.project__body__box}>
                <h2 className={classes.project__body__title}>Events</h2>
              </div>
              <div className={classes.project__news}>
                {data.map((event) => (
                  <div className={classes.project__news__box}>
                    <Link to={`/event/${event?.slug || event?._id}`}>
                      <div className={classes.project__news__box__cover}>
                        {event.bannerImage ? (
                          <img
                            src={event.bannerImage}
                            alt=""
                            style={{ height: 160, objectFit: "cover" }}
                          />
                        ) : null}
                        <div className={classes.project__news__box__content}>
                          <h2
                            className={
                              classes.project__news__box__content__title
                            }
                          >
                            {event.title}
                          </h2>
                          <ClampText>
                            <p
                              dangerouslySetInnerHTML={{
                                __html: event.summary || "",
                              }}
                            />
                          </ClampText>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className={classes.project__sidebar}>
          <div className={classes.project__sidebar__wrapper}>
            <div className={classes.project__sidebar__wrapper__title}>
              <p>Event Details</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Descriptions column={1} size="small">
                {data.location && (
                  <Descriptions.Item label="Location">
                    {data.location}
                  </Descriptions.Item>
                )}
                {data.startDate && (
                  <Descriptions.Item label="Start">
                    {new Date(data.startDate).toLocaleDateString()}
                  </Descriptions.Item>
                )}
                {data.endDate && (
                  <Descriptions.Item label="End">
                    {new Date(data.endDate).toLocaleDateString()}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          </div>

          {data.project && (
            <div
              className={classes.project__sidebar__wrapper}
              style={{ cursor: "pointer" }}
            >
              <Link to={`/capacity-projects/${data.project?._id}`}>
                <div
                  className={classes.project__sidebar__wrapper__title}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <p>{data.project?.name}</p>
                  {data.project?.logo && (
                    <img
                      src={data.project.logo}
                      alt=""
                      style={{ width: 100, height: 60, objectFit: "contain" }}
                    />
                  )}
                </div>
                {data.project?.overviewHtml && (
                  <ClampText>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: data.project.overviewHtml,
                      }}
                    />
                  </ClampText>
                )}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
